/**
 * Features Section Component
 *
 * Grid layout showcasing product features with icons, headlines, and descriptions.
 * Built with Lightning Velocity design tokens.
 */

import React from 'react';
import { clsx } from 'clsx';

export interface Feature {
  /**
   * Feature icon (React element or emoji)
   */
  icon: React.ReactNode;

  /**
   * Feature headline
   */
  title: string;

  /**
   * Feature description
   */
  description: string;

  /**
   * Optional link to learn more
   */
  link?: {
    text: string;
    href: string;
  };
}

export interface FeaturesProps {
  /**
   * Section heading
   */
  heading?: string;

  /**
   * Section subheading
   */
  subheading?: string;

  /**
   * List of features to display
   */
  features: Feature[];

  /**
   * Number of columns (2, 3, or 4)
   */
  columns?: 2 | 3 | 4;

  /**
   * Background variant
   */
  background?: 'white' | 'gray' | 'gradient';

  /**
   * Card style variant
   */
  cardStyle?: 'flat' | 'elevated' | 'outlined';
}

/**
 * Features grid section using Lightning Velocity design system
 */
export const Features: React.FC<FeaturesProps> = ({
  heading,
  subheading,
  features,
  columns = 3,
  background = 'white',
  cardStyle = 'flat',
}) => {
  // Background styles
  const backgroundStyles = {
    white: 'bg-white',
    gray: 'bg-neutral-50',
    gradient: 'bg-gradient-to-b from-white to-neutral-50',
  };

  // Grid columns
  const gridColumns = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-2 lg:grid-cols-3',
    4: 'md:grid-cols-2 lg:grid-cols-4',
  };

  // Card styles
  const cardStyles = {
    flat: 'bg-transparent',
    elevated: 'bg-white shadow-md hover:shadow-lg transition-shadow duration-base',
    outlined: 'bg-white border-2 border-neutral-200 hover:border-brand-primary transition-colors duration-base',
  };

  return (
    <section className={clsx(
      'py-16 md:py-24',
      backgroundStyles[background]
    )}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        {(heading || subheading) && (
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
            {subheading && (
              <p className="text-brand-primary font-semibold text-sm uppercase tracking-wide mb-3">
                {subheading}
              </p>
            )}
            {heading && (
              <h2 className="text-display-l font-display font-bold text-neutral-900 mb-4">
                {heading}
              </h2>
            )}
          </div>
        )}

        {/* Features Grid */}
        <div className={clsx(
          'grid grid-cols-1 gap-8',
          gridColumns[columns]
        )}>
          {features.map((feature, index) => (
            <div
              key={index}
              className={clsx(
                'rounded-lg p-6 md:p-8',
                'animate-fade-in',
                cardStyles[cardStyle]
              )}
              style={{
                animationDelay: `${index * 100}ms`,
              }}
            >
              {/* Icon */}
              <div className="mb-4 flex items-center justify-center w-12 h-12 rounded-lg bg-brand-primary/10 text-brand-primary">
                {typeof feature.icon === 'string' ? (
                  <span className="text-2xl">{feature.icon}</span>
                ) : (
                  feature.icon
                )}
              </div>

              {/* Title */}
              <h3 className="text-h2 font-display font-semibold text-neutral-900 mb-3">
                {feature.title}
              </h3>

              {/* Description */}
              <p className="text-base text-neutral-600 leading-relaxed mb-4">
                {feature.description}
              </p>

              {/* Optional Link */}
              {feature.link && (
                <a
                  href={feature.link.href}
                  className="inline-flex items-center gap-1 text-brand-primary hover:text-brand-primary-dark font-medium transition-colors duration-base"
                >
                  {feature.link.text}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

Features.displayName = 'Features';

export default Features;
